// ContentPanel.js
import React, { useState, useEffect, useMemo } from 'react';

import { Box, 
        CircularProgress, 
        IconButton, 
        Paper,
        Menu,
        Dialog, 
        DialogTitle, 
        DialogContent, 
        DialogContentText, 
        DialogActions, 
        Button } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { FaTrashAlt, FaEllipsisV } from 'react-icons/fa';
import { useConfig, configKeys } from './ConfigHolder';
import { loadObjectFromServer} from './contentloader';
import {Sentence} from './models/Sentence'
import { sentenceFactory } from './models/sentenceFactory';
import _  from 'lodash';
import ContentChoice from './ContentChoice';
import { useAuth } from './AuthProvider';
import OnPlanEndErrorHandler from './OnPlanEndErrorHandler';
import CloseIcon from '@mui/icons-material/Close';

import ShowContent from './ShowContent';
import Conversations from './Conversations';
import { useAutoPlay } from './contexts/AutoPlayContext';
import { BasicEvents, BasicStates } from './fsm/AutoPlayBaseFSM';
import Tooltip from '@mui/material/Tooltip'; // Import Tooltip
import ContentGrid from './ContentGrid';
// Import SVGs as components
import { ReactComponent as JustPlayStartIcon } from "./resources/JustPlayStart.svg";
import { ReactComponent as JustPlayStopIcon } from "./resources/JustPlayStop.svg";
import AutoplayTimingControls from './components/AutoplayTimingControls1';
import PlaybackSpeedControls from './components/PlaybackSpeedControls1';

// import ShowContentVirtualized from './ShowContentVirtualized';

const isReplaceable = (phraseObj, sentence) => {
  const k_phrase = phraseObj.phrase;
  const k_s = sentence.sentence;
  return (k_s === k_phrase) && !_.isEqual(phraseObj, sentence.phraseObj);
}

const alreadyThere= (content, sentence) => {
  for (const phraseObj of content.phrases) {
    if (phraseObj.phrase === sentence.sentence) {
      return true;
    }
  }
  return false;
}

// Keep content.sentences aligned with content.phrases
const syncSentences = (content) => {
  if (!content || !Array.isArray(content.phrases)) return content;

  const sentences = content.phrases.map((_, idx) =>
    sentenceFactory(content, idx, { dontUpdateContent: true })
  );

  return { ...content, sentences };
};

const getMyContentInsertIndex = (content, newSentence, fallbackIndex = 0) => {
  const phraseCount = Array.isArray(content?.phrases) ? content.phrases.length : 0;
  if (newSentence?.insertMode === 'top') {
    return 0;
  }

  const anchorIndex = Number.isInteger(newSentence?.anchorIndex)
    ? newSentence.anchorIndex
    : fallbackIndex;

  if (!Number.isInteger(anchorIndex) || anchorIndex < 0 || anchorIndex >= phraseCount) {
    return 0;
  }

  if (newSentence?.insertMode === 'below') {
    return Math.min(anchorIndex + 1, phraseCount);
  }

  return anchorIndex;
};

/**
 * Update the content object with a new or edited Sentence instance.
 *
 * @param {Object} content - Current content object. Expected shape: { phrases: Array<Object>, ... }.
 * @param {Sentence} newSentence - Sentence instance containing the updated/new phrase data.
 * @param {number} [index] - Optional index used when adding to DB (passed to newSentence.updateAttributes).
 * @returns {Object} A content object to use for state. This function never mutates the original content:
 *                   it returns the original reference when no changes are required to avoid unnecessary re-renders,
 *                   or a new object when changes must be applied.
 *
 * Behavior summary:
 * - If newSentence?.dontUpdateContent is truthy, return the original content unchanged.
 * - If newSentence?.addToMyContent is truthy:
 *     - If the phrase text already exists in content, return original content.
 *     - Otherwise call newSentence.updateAttributes(content, index) and newSentence.addSentenceDB()
 *       (DB errors are caught and logged), then return a new content with the new phrase prepended.
 * - Otherwise, if content contains a phrase with the same text as newSentence.sentence:
 *     - If an existing phrase is deeply equal to newSentence.phraseObj, return original content.
 *     - If not equal, return a new content object where the matching phrase is replaced by newSentence.phraseObj.
 * - In all other cases return the original content.
 *
 * Notes:
 * - Designed to be idempotent: when no logical change is required the original content object is returned.
 * - Catches DB errors and logs them; in case of failure the original content is returned.
 * - Keeps component update behavior predictable by avoiding unnecessary object identity changes.
 */
const updateContent = (content, newSentence, index) => {
  // this function is not making any changes to the existing object
  // It creates a new object when necessary and then the caller calls the state setter
  const addToMyContentDB = () => {
    const insertIndex = getMyContentInsertIndex(content, newSentence, index);
    newSentence.updateAttributes(content, insertIndex);
    try {
      newSentence.addSentenceDB();
    } catch (err) {
      console.error('Failed to add phrase to DB', err);
      return content;
    }
    const updated = {
      ...content,
      phrases: [
        ...content.phrases.slice(0, insertIndex),
        newSentence.phraseObj,
        ...content.phrases.slice(insertIndex),
      ],
    };
    return syncSentences(updated);
  };

  const existsAndEqual = () => {
    for (const phraseObj of content.phrases) {
      if ((phraseObj.phrase === newSentence.sentence) && _.isEqual(phraseObj, newSentence.phraseObj)) {
        return true;
      }

    }
    return false;
  }

  if (newSentence?.dontUpdateContent) {
    return content;
  }

  if (newSentence?.addToMyContent) {
    if (alreadyThere(content, newSentence)) {
      return content;
    }
    return addToMyContentDB();
  };


  if (! _.isEmpty(content) && 
     newSentence && 
     newSentence.sentence && 
     !(content.phrases.length === 0) &&
     alreadyThere(content, newSentence)) {
     if (existsAndEqual()) {
      return content;
     }
     const newPhrases = content.phrases.map((phraseObj) => (
        isReplaceable(phraseObj, newSentence) ? newSentence.phraseObj : phraseObj
      ));
      // If deep-equal, nothing actually changed — return original content to avoid
      // unnecessary reference updates which trigger recomputations.
      if (_.isEqual(newPhrases, content.phrases)) {
        return content;
      }
      const updated = {
        ...content,
        phrases: newPhrases,
      };
      return syncSentences(updated);
  }

  // Ensure a stable return value — return original content when no changes apply
  return content;
};

/**
 * ContentPanel
 *
 * Parent component that manages loading and updating content and renders the ShowContent table.
 *
 * Props:
 *  @param sentenceCallBack: function(Sentence) -> void
 *      Called to update the currently active sentence in the app.
 *  @param sentenceValue: Sentence
 *      The current sentence object passed from parent (used to update content when modified).
 *  @param errorCallBack: function(Error) -> void
 *      Optional centralized error handler.
 *
 * Responsibilities:
 *  - Fetch content from server when language/content config changes.
 *  - Integrate newly added/edited sentences into content.
 *  - Provide controls for autoplay and content selection/deletion.
 *
 * Notes / Recommendations:
 *  - Consider adding PropTypes (or converting to TypeScript) for stronger guarantees.
 *  - Remove or guard console.time/console.log statements for production.
 *  - Validate contentSelectedIndex before deleting/updating.
 */



const ContentPanel = ({ sentenceCallBack, sentenceValue, errorCallBack, myContentEmptyCallBack }) => {
  
  const { config, updateConfig } = useConfig();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  // contentSelectedIndex is the index of the currently selected content
  // in the ShowContent table. It is used for delete operation of the selected sentence in SchowContent
  const [contentSelectedIndex, setContentSelectedIndex] = useState(-1);
  const {logout} = useAuth();
  const theme = useTheme();
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [contentIndexToSelect, setContentIndexToSelect] = useState(null);
  const [contentView, setContentView] = useState('contentTable');
  const [conversationOpen, setConversationOpen] = useState(false);
  const [timingMode, setTimingMode] = useState('fixedDelay');
  const [fixedDelaySeconds, setFixedDelaySeconds] = useState(1.0);
  const [autoplayPlaybackRate, setAutoplayPlaybackRate] = useState(1.0);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const  ctx  = useAutoPlay(); // <-- use context

  const langLearn = config[configKeys.langLearn];
  const langBase = config[configKeys.langBase];
  const currentContent = config[configKeys.currentContent];

  useEffect(() => {
    if (typeof myContentEmptyCallBack !== 'function') return;

    const isEmptyMyContent =
      currentContent === 'MyContent' &&
      Array.isArray(content?.phrases) &&
      content.phrases.length === 0;

    myContentEmptyCallBack(isEmptyMyContent);
  }, [content?.phrases, currentContent, myContentEmptyCallBack]);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const llabel = config[configKeys.langLearn].label;
      const blabel = config[configKeys.langBase].label;
      const content_type = config[configKeys.currentContent];
      // If the selected content type references conversations, switch the view
      if (typeof content_type === 'string' ) {
        if (content_type.toLowerCase().includes('conversations')) {
          setContentView('conversations');
        } else {
          setContentView('contentTable');
        }
      }

      try {
        // sentenceCallBack( new Sentence(), null);
        const result = await loadObjectFromServer(llabel, blabel, content_type, logout);
        setContent(result);
      } catch (error) {
        console.error('Failed to fetch content', error);
        if (errorCallBack) { errorCallBack(error); } 
            else {
                setError(error);
            }
      }
      setLoading(false);
    };
    fetchData();
  // eslint-disable-next-line
  }, [langLearn, langBase, currentContent]);

  useEffect(() => {
    const updateSentenceContent = async () => {
      if (sentenceValue && sentenceValue.sentence) {
        try {
          // await updateConfig(configKeys.currentContent, 'MyContent');
          console.time("updateContent")
          const insertAnchorIndex = sentenceValue.addToMyContent
            ? contentSelectedIndex
            : 0;
          const newContent = updateContent(content, sentenceValue, insertAnchorIndex);
          console.timeEnd("updateContent")
          if (sentenceValue.addToMyContent && newContent !== content) {
            setContentIndexToSelect(sentenceValue.index);
            setContentSelectedIndex(sentenceValue.index);
          }
          setContent(newContent);
        } catch (error) {
          console.error('Failed to update content with new sentence:', error);
        }
      }
    };
    const contentIndex = sentenceValue?.index;
    if (!sentenceValue?.addToMyContent) {
      setContentSelectedIndex(contentIndex)
      setContentIndexToSelect(contentIndex)
    }
    updateSentenceContent();
    // eslint-disable-next-line
  }, [sentenceValue]); // it is sentece object passed as a prompt from MainContent.

  const allContentTags = useMemo(() => {
    console.time("listTags");

    // Use a Map to preserve display case but avoid case duplicates
    const tagMap = new Map();

    content?.phrases?.forEach((phraseObj) => {
      phraseObj.tags?.forEach((tag) => {
        const key = tag.toLowerCase();
        if (!tagMap.has(key)) tagMap.set(key, tag);
      });
    });

    // Preserve display form, sort alphabetically ignoring case
    const res = Array.from(tagMap.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    // console.log("tags", res);
    console.timeEnd("listTags");
    return res;
  }, [content]);

  
  const handleContentChoiceChange = async (event) => {
    // console.log(event);
    const contentType = event?.target?.value;
  
    // Ignore undefined values
    if (contentType === undefined) {
      console.log("No selection made yet or submenu triggered.");
      return;
    }

    sentenceCallBack ( new Sentence(), null)
    if (contentType === "numbers") {
      console.log("submenu is triggered.");
      return;
    }
  
    console.log(`Selected: ${contentType}`); 
    try {
      await updateConfig(configKeys.currentContent, contentType)

    }
    catch (err) {
       if (errorCallBack) { errorCallBack(err); } 
            else {
                setError(err);
            }
      console.error('Failed to update config', err);
    }
  };
  
  
  const showIcons = () => { 

    return (config[[configKeys.currentContent]].includes("MyContent")) && ! ctx.ui.contentTableDisabled
  }

  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    let sentenceRemove = new Sentence(content, contentSelectedIndex)
    const newContentBase = {
      ...content,
      phrases: [
        ...content.phrases.slice(0, contentSelectedIndex),
        ...content.phrases.slice(contentSelectedIndex + 1),
      ],
    };
    const newContent = syncSentences(newContentBase);
    setContent(newContent);
    let newContentIndex = null;
    if (typeof sentenceValue?.previousFilteredOriginal === 'number') {
      const candidate = sentenceValue.previousFilteredOriginal;
      if (candidate >= 0 && candidate < newContent.phrases.length) {
        newContentIndex = candidate;
      }
    }

    // fallback: choose previous row by numeric position relative to deleted index,
    // otherwise pick first row if any, otherwise null
    if (newContent.phrases.length > 0 && (newContentIndex === null)) {
      newContentIndex = contentSelectedIndex > 0 ? contentSelectedIndex - 1 : 0;
    } else if (newContent.phrases.length === 0) {
      newContentIndex = null;
    }

    const newSentence = new Sentence(newContent, newContentIndex, {play: false})
    sentenceCallBack( newSentence);
    // remove from the DB
    sentenceRemove.deleteSentenceDB();
    setDeleteConfirmed(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const autoPlayDisabled = () => {
    if (!content) return true;

    // Conversations mode: disable when only the list is shown, enable when a conversation is open
    if (contentView === 'conversations') {
      if (!conversationOpen) return true;
      return !Array.isArray(content.sentences) || content.sentences.length === 0;
    }

    // Default: require phrases for autoplay
    return !Array.isArray(content.phrases) || content.phrases.length === 0;
  }

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        borderRadius: 3,
        position: 'relative',
        pl: 1,
        width: '100%',
        margin: '0 auto',
        backgroundColor: theme.customColors.bgContent,
        display:'flex',
        flexDirection:'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          pt: 2,
          my: 0,
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top controls: NOT scrollable */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: theme.customColors.bgContent,
            }}
          >
            {/* <ContentChoice
              config={config}
              configKeys={configKeys}
              handleContentChoiceChange={handleContentChoiceChange}
              disabled={ctx.ui.contentTableDisabled}
            /> */}
            <ContentGrid 
              contentPath={config[configKeys.currentContent]}
              disabled={ctx.ui.contentTableDisabled}
              handleContentChoiceChange={handleContentChoiceChange}
            /> 
            <Tooltip title={ctx.autoPlayState === BasicStates.IDLE ? "Start AutoPlay" : "Stop AutoPlay"}>
              <span style={{ display: 'inline-block' }}>
                <IconButton
                  onClick={() => {
                    ctx.autoPlayState === BasicStates.IDLE
                      ? ctx.dispatch({ type: BasicEvents.START })
                      : ctx.dispatch({ type: BasicEvents.STOP });
                  }}
                  disabled={autoPlayDisabled()}
                  sx={{ ml: 2, my: 0, fontSize: 16, p:0 }}
                >
                  {ctx.autoPlayState === BasicStates.IDLE
                    ? <JustPlayStartIcon style={{ width: 56, height: 56, opacity: autoPlayDisabled() ? 0.3 : 1 }} />
                    : <JustPlayStopIcon style={{ width: 56, height: 56, opacity: autoPlayDisabled() ? 0.3 : 1 }} />
                  }
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              {showIcons() && (
                <Tooltip title="Delete Selected Sentence">
                  <span style={{ display: 'inline-block' }}>
                    <IconButton onClick={handleDeleteClick} disabled={contentSelectedIndex < 0}>
                      <FaTrashAlt color={contentSelectedIndex >= 0 ? 'blue' : 'gray'} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="More options">
                <span style={{ display: 'inline-block' }}>
                  <IconButton
                    onClick={handleMoreMenuOpen}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <FaEllipsisV />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Menu
          anchorEl={moreMenuAnchorEl}
          open={Boolean(moreMenuAnchorEl)}
          onClose={handleMoreMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              minWidth: 240,
            },
          }}
          BackdropProps={{
            invisible: false,
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
          }}
        >
          <AutoplayTimingControls
            timingMode={timingMode}
            setTimingMode={setTimingMode}
            fixedDelaySeconds={fixedDelaySeconds}
            setFixedDelaySeconds={setFixedDelaySeconds}
            triggerMode="menuItem"
            triggerLabel="Autoplay pace"
          />
          <PlaybackSpeedControls
            playbackRate={autoplayPlaybackRate}
            setPlaybackRate={setAutoplayPlaybackRate}
            triggerMode="menuItem"
            triggerLabel="Playback speed"
          />
        </Menu>

        {/* Scrollable ShowContent */}
        <Box sx={{
          // height: '80vh', // Adjust as needed
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          mt: 0,
          borderRadius: 2,
          backgroundColor: theme.customColors.bgContent,
        }}>
          {!loading && (
      contentView === 'conversations' ? (
      <Conversations 
        content={content}
        sentenceCallBack={sentenceCallBack}
        autoPlayState={ctx.autoPlayState} 
        errorCallBack={errorCallBack} 
        allContentTags = {allContentTags}
        onConversationSelectionChange={setConversationOpen}
        timingMode={timingMode}
        fixedDelaySeconds={fixedDelaySeconds}
        autoplayPlaybackRate={autoplayPlaybackRate}
      />
          ) : (
            // ShowContent is rendered for 'contentView' and all other cases to preserve existing behavior
            <ShowContent
              content={content}
              sentenceCallBack={sentenceCallBack}
              autoPlayState={ctx.autoPlayState}
              errorCallBack = {errorCallBack}
              allContentTags = {allContentTags}
              contentIndex = {contentIndexToSelect}
              deleteConfirmed = {deleteConfirmed}
              deleteConfirmedCallBack = {(val)=>{setDeleteConfirmed(val)}}
              timingMode={timingMode}
              fixedDelaySeconds={fixedDelaySeconds}
              autoplayPlaybackRate={autoplayPlaybackRate}
            />
          ))}
        </Box>

        {error && (
          <OnPlanEndErrorHandler
            error={error}
            navigateToLogin={true}
            showDialog={true}
          />
        )}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          PaperProps={{
              sx: { 
                  backgroundColor: 'seashell', 
                  borderRadius: 2, 
                  p: 2 
              }
            }}
         >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Delete Sentence
            <IconButton
              aria-label="close"
              onClick={handleDeleteCancel}
              sx={{
                color: (theme) => theme.palette.grey[500],
                ml: 2,
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete sentence{' '}
              <span style={{ color: 'blue', fontStyle: 'italic' }}>
                "{sentenceValue?.sentence ?? ''}"
              </span>
              ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              sx={{ ml: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

    </Paper>
  );
};

export default ContentPanel;
